import { useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { scrollToSection } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CITIES = ["Montréal", "Ville de Québec", "Mont-Tremblant", "Gatineau", "Charlevoix", "Sherbrooke", "Magog"];
const TYPES = ["Studio / 3 ½", "4 ½ (2 chambres)", "Condo 3 chambres", "Chalet d'exception"];
const CITY_BASE = { "Montréal": 175, "Ville de Québec": 210, "Mont-Tremblant": 350, "Gatineau": 110, "Charlevoix": 290, "Sherbrooke": 150, "Magog": 195 };
const TYPE_MULT = { "Studio / 3 ½": 0.75, "4 ½ (2 chambres)": 1, "Condo 3 chambres": 1.35, "Chalet d'exception": 2.6 };

const eur = (n) => new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(n);

const selectCls =
    "w-full appearance-none rounded-lg border border-[#8A7563]/20 bg-[#FAF8F5]/70 px-4 py-3 text-sm text-[#2B2622] transition-colors focus:border-[#8A7563]/60 focus:outline-none";

export default function RevenueSimulator({ onEstimate }) {
    const [city, setCity] = useState("Montréal");
    const [type, setType] = useState("4 ½ (2 chambres)");
    const [surface, setSurface] = useState(55);
    const [nights, setNights] = useState(18);
    const rate = 0.12;
    const [sending, setSending] = useState(false);

    const est = useMemo(() => {
        const nightly = Math.max(40, Math.round(CITY_BASE[city] * TYPE_MULT[type] * (1 + (surface - 30) * 0.004)));
        const occupied = Math.min(30, Math.round(nights * 1.28));
        const gross = nightly * occupied;
        const commission = Math.round(gross * rate);
        return { nightly, occupied, gross, commission, net: gross - commission };
    }, [city, type, surface, nights, rate]);

    const handleEstimate = async () => {
        setSending(true);
        try {
            await axios.post(`${API}/simulate`, {
                city,
                property_type: type,
                surface,
                nights,
                rate,
            });
        } catch {
            /* non-bloquant : l'estimation reste affichée */
        }
        onEstimate(
            `Bonjour, je souhaite une estimation détaillée pour mon bien : ${type} de ${surface} m² à ${city} (${nights} nuits/mois envisagées). Revenu net estimé par votre simulateur : ${eur(est.net)}/mois. Merci de me recontacter.`,
        );
        toast.success("Estimation prête — il ne reste qu'à nous laisser vos coordonnées.");
        scrollToSection("#contact");
        setSending(false);
    };

    return (
        <section id="simulateur" data-testid="simulator-section" className="relative overflow-hidden py-28">
            <div className="gold-spotlight pointer-events-none absolute inset-0" />
            <div className="relative mx-auto max-w-7xl px-6">
                <Reveal>
                    <p className="eyebrow mb-6 flex items-center gap-3">
                        <span className="inline-block h-px w-10 bg-[#8A7563]" />
                        Simulateur de revenus
                    </p>
                    <h2 className="font-display max-w-2xl text-3xl font-semibold leading-tight sm:text-4xl lg:text-5xl">
                        Combien votre bien peut-il <span className="italic text-[#8A7563]">vraiment</span> rapporter ?
                    </h2>
                </Reveal>

                <div className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1fr]">
                    <Reveal delay={0.1}>
                        <div className="rounded-2xl border border-[#2B2622]/10 bg-[#FFFFFF]/70 p-8 backdrop-blur-xl">
                            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                                <label className="block">
                                    <span className="mb-2 block font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#8A7F72]">Ville</span>
                                    <select data-testid="simulator-city-select" value={city} onChange={(e) => setCity(e.target.value)} className={selectCls}>
                                        {CITIES.map((c) => (
                                            <option key={c}>{c}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="block">
                                    <span className="mb-2 block font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#8A7F72]">Type de bien</span>
                                    <select data-testid="simulator-property-type" value={type} onChange={(e) => setType(e.target.value)} className={selectCls}>
                                        {TYPES.map((t) => (
                                            <option key={t}>{t}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="block">
                                    <span className="mb-2 block font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#8A7F72]">Surface (m²)</span>
                                    <input
                                        data-testid="simulator-surface-input"
                                        type="number"
                                        min="10"
                                        max="1000"
                                        value={surface}
                                        onChange={(e) => setSurface(Math.max(10, Math.min(1000, Number(e.target.value) || 10)))}
                                        className={selectCls}
                                    />
                                </label>
                            </div>

                            <div className="mt-8">
                                <div className="mb-3 flex items-center justify-between">
                                    <span className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#8A7F72]">
                                        Nuits louées visées / mois
                                    </span>
                                    <span className="font-mono-accent text-sm text-[#8A7563]">{nights} nuits</span>
                                </div>
                                <input
                                    data-testid="simulator-nights-slider"
                                    type="range"
                                    min="5"
                                    max="30"
                                    value={nights}
                                    onChange={(e) => setNights(Number(e.target.value))}
                                    className="w-full"
                                    style={{ "--fill": `${((nights - 5) / 25) * 100}%` }}
                                />
                            </div>

                            <p className="mt-8 flex items-start gap-2 text-xs leading-relaxed text-[#8A7F72]">
                                <TrendingUp size={14} className="mt-0.5 shrink-0 text-[#10B981]" />
                                Projection basée sur les prix moyens des annonces Airbnb comparables de la région
                                (données publiques 2025-2026) et sur notre taux d'occupation constaté (+28 % vs une annonce
                                gérée en direct). Estimation indicative, affinée lors de l'étude gratuite de votre bien.
                            </p>
                        </div>
                    </Reveal>

                    <Reveal delay={0.2}>
                        <div className="flex h-full flex-col justify-between rounded-2xl border border-[#8A7563]/25 bg-gradient-to-b from-[#FFFFFF] to-[#FAF8F5] p-8 shadow-[0_24px_80px_rgba(90,79,68,0.18)]">
                            <div className="space-y-7">
                                <div>
                                    <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#8A7F72]">Revenu net estimé</p>
                                    <motion.p
                                        key={est.net}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.4 }}
                                        data-testid="simulator-result-host-net"
                                        className="font-display mt-2 text-5xl font-semibold text-[#8A7563] sm:text-6xl"
                                    >
                                        {eur(est.net)}
                                        <span className="ml-2 align-middle font-mono-accent text-xs uppercase tracking-[0.2em] text-[#8A7F72]">/ mois</span>
                                    </motion.p>
                                </div>
                                <div className="grid grid-cols-3 gap-4 border-t border-[#2B2622]/10 pt-6">
                                    <div>
                                        <p className="font-mono-accent text-[9px] uppercase tracking-[0.2em] text-[#8A7F72]">Prix / nuit</p>
                                        <p data-testid="simulator-result-nightly" className="mt-1.5 text-lg font-semibold">{eur(est.nightly)}</p>
                                    </div>
                                    <div>
                                        <p className="font-mono-accent text-[9px] uppercase tracking-[0.2em] text-[#8A7F72]">Brut mensuel</p>
                                        <p data-testid="simulator-result-gross" className="mt-1.5 text-lg font-semibold">{eur(est.gross)}</p>
                                    </div>
                                    <div>
                                        <p className="font-mono-accent text-[9px] uppercase tracking-[0.2em] text-[#8A7F72]">Notre commission</p>
                                        <p data-testid="simulator-result-commission" className="mt-1.5 text-lg font-semibold text-[#8A7563]">{eur(est.commission)}</p>
                                    </div>
                                </div>
                            </div>
                            <button
                                data-testid="simulator-cta-button"
                                onClick={handleEstimate}
                                disabled={sending}
                                className="group mt-10 flex w-full items-center justify-center gap-3 rounded-full bg-[#8A7563] px-7 py-4 text-sm font-semibold text-[#FAF8F5] transition-all duration-300 hover:bg-[#6E5D4E] hover:shadow-[0_0_32px_rgba(138,117,99,0.35)] disabled:opacity-60"
                            >
                                {sending ? "Préparation…" : "Recevoir mon estimation détaillée"}
                                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                            </button>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
